import _ from 'lodash';
import React, { ReactElement } from 'react';
import HomeIcon from '@material-ui/icons/Home';
import ShareIcon from '@material-ui/icons/Share';
import { getDistanceFromLatLonInKm } from '../../utils/mapUtils';
import { API_UPDATE_DRIVER_ACTIVATION, DRIVERS_RAYON } from '../../constants';
import { getIdToken } from '../../apis/firebase';
import { updateCollection } from '../../apis/shop.service';
import { sendPostRequest } from '../../apis/backend';
import { sendCloudWatchAlert } from '../../utils/applicationError';
import { isFreelanceEnabled } from 'src/utils/delivery';
import { DishopDriver, DriverCollection, ExtendedDriverCollection, ExtendedDriverData } from 'src/models/drivers/driver';
import { DriversViewState } from './Drivers.view';
import { DRIVER_TABLE_HEAD } from 'src/constants/table';
import { initPage } from 'src/actions';

export type NavigationIcons = {
    icon: ReactElement,
    label: string
}

export type DriversViewProps = {
    location: {
        latitude: number,
        longitude: number
    }
    shopId: string
}

export const getBottomNavigationIcon = (driverTypes: string[]) => {
    const newData: NavigationIcons[] = [];
    _.map(driverTypes, (driverType: string) => {
        newData.push({
            label: driverType,
            icon: driverType === 'Employés' ? <HomeIcon /> : <ShareIcon />
        });
    });
    return newData;
};

export const handleDriversData = (
    drivers: DriverCollection,
    independentDrivers: DriverCollection,
    selectedTab: number,
    setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>,
    props: DriversViewProps) => {

    const returnDrivers = initDrivers(drivers, false, props);
    const returnIndependentDrivers = initDrivers(independentDrivers, true, props);
    setComponentState(prevState => ({
        ...prevState,
        isLoading: false,
        currentDrivers: selectedTab > 0 ? returnIndependentDrivers : returnDrivers
    }));
};

const initDrivers = (driversList: DriverCollection, isIndependent: boolean, props: DriversViewProps) => {
    const returnDrivers: ExtendedDriverCollection = {};
    driversList &&
        _.map(driversList, (driver: DishopDriver, driverKey: string) => {
            const driverShop = driver?.shopIds?.[props.shopId];
            let enabled = driverShop?.enabled || false
            if (isIndependent && !_.has(driverShop, 'enabled')) enabled = true;
            const driverData: ExtendedDriverData = {
                ...driver,
                id: driverKey,
                nbOrderSuccess: driverShop?.completedOrders || 0,
                speed: 0,
                distance: 0,
                duration: 0,
                isDriverClose: getDistanceFromLatLonInKm(driver.lat, driver.long, props.location.latitude, props.location.longitude) < DRIVERS_RAYON,
                enabled
            };
            driverData.onTransaction = setDriverDataOnTransactionValue(driverData);
            const isDriverAvailable = checkDriverAvailability(driverData, props.shopId, driverShop?.enabled);
            if (isDriverAvailable) {
                returnDrivers[driverKey] = { ...driverData };
            }
        });
    return returnDrivers;
};

const checkDriverAvailability = (driver: ExtendedDriverData, shopId: string, enabledEmployee: boolean) => {
    if (driver?.email) {
        return driver?.isDriverClose || (driver?.shopIds?.[shopId]?.completedOrders > 1 || enabledEmployee);
    }
    return false;
}

const setDriverDataOnTransactionValue = (driverData: ExtendedDriverData) => {
    if (driverData?.status === 'delivering') {
        return 'En livraison';
    }
    if (driverData?.status && driverData.isDriverClose) {
        return 'Disponible';
    }
    if (!driverData?.tokenNotifications) {
        return 'Application désinstallée';
    }
    if (!driverData?.freelance?.verification && driverData?.isFreelance) {
        return 'Documents requis';
    }
    else {
        return 'Non disponible';
    }
}

export const showDriversTypeButton = (setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>, shopId: string) => {
    const newListButton = ['Employés'];
    if (isFreelanceEnabled(shopId)) {
        newListButton.push('Indépendants');
    }
    setComponentState(prevState => ({
        ...prevState,
        listButton: newListButton,
        selectedTab: 0
    }));
};

export const onChangeData = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: number,
    independentDrivers: ExtendedDriverCollection,
    drivers: ExtendedDriverCollection,
    setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>,
    dispatch,
    props: DriversViewProps
) => {
    const returnDrivers = initDrivers(drivers, false, props);
    const returnIndependentDrivers = initDrivers(independentDrivers, true, props);
    dispatch(initPage());
    e.preventDefault();
    setComponentState(prevState => ({
        ...prevState,
        selectedTab: value,
        currentDrivers: value === 1 ? returnIndependentDrivers : returnDrivers
    }))
};

export const defineDriversTableHeads = (isIndependentTab: boolean, canToggleDrivers: boolean) => {
    if (!isIndependentTab && canToggleDrivers) {
        return DRIVER_TABLE_HEAD;
    }
    const idsToRemove: string[] = [];
    if (!canToggleDrivers) {
        idsToRemove.push('enabled');
    }
    if (isIndependentTab) {
        idsToRemove.push('cash');
    }
    return DRIVER_TABLE_HEAD.filter(item => !idsToRemove.includes(item.id));
}

export const switchDriverActivation = async (
    driverId: string,
    shopId: string,
    freelance: boolean,
    enabled: boolean,
    currentDrivers: ExtendedDriverCollection,
    setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>
) => {

    const idToken = await getIdToken();
    try {
        await sendPostRequest(
            API_UPDATE_DRIVER_ACTIVATION,
            {
                shopId,
                driverId,
                freelance,
                enabled
            },
            { Authorization: `Bearer ${idToken}` }
        );
        if (currentDrivers[driverId]) {
            currentDrivers[driverId].enabled = enabled;
            setComponentState(prevState => ({ ...prevState, ...currentDrivers }))
        }
    } catch (err) {
        sendCloudWatchAlert(`Error in switchDriverActivation - Drivers.services: ${err}`);
    }
};

export const handleSubmit = async (
    driver: ExtendedDriverData,
    currentDrivers: ExtendedDriverCollection,
    setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>) => {

    try {
        const { cash, cb, ticket_resto } = driver;
        await updateCollection(driver?.id, { cash, cb, ticket_resto });
        currentDrivers[driver.id] = { ...currentDrivers[driver.id], cb, cash, ticket_resto }
        setComponentState(prevState => ({ ...prevState, modalOpen: false, showModalUpdateDriver: false, currentDrivers }));
    } catch (err) {
        sendCloudWatchAlert(`Error updating driver collection : ${err}`);
    }
};

export const handleCloseModal = (setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>) => {
    setComponentState(prevState => ({ ...prevState, modalOpen: false }));
};

export const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setSelectedDriver: React.Dispatch<React.SetStateAction<ExtendedDriverData | null>>) => {

    const numericValue = Number(e.target.value) < 0 ? 0 : Number(e.target.value);
    setSelectedDriver(prevState => {
        if (!prevState) return null;
        return { ...prevState, [e.target.id]: numericValue };
    });
};